import React, { useState } from 'react';
import styles from '../../pages/CheckoutPage.module.css';

function formatExpiry(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

// Luhn algorithm
function luhn(number) {
  const digits = number.replace(/\D/g, '');
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alt) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

// Detect card type from number prefix
function detectCardType(number) {
  const n = number.replace(/\s/g, '');
  if (/^4/.test(n)) return { type: 'Visa', icon: '💳', cvvLen: 3 };
  if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) return { type: 'Mastercard', icon: '💳', cvvLen: 3 };
  if (/^3[47]/.test(n)) return { type: 'Amex', icon: '💳', cvvLen: 4 };
  if (/^6(0|22|4|5)/.test(n)) return { type: 'Discover', icon: '💳', cvvLen: 3 };
  if (/^(508[5-9]|6069|607|608|6521|6522)/.test(n)) return { type: 'RuPay', icon: '🏦', cvvLen: 3 };
  return null;
}

// Validate expiry: not in the past
function validateExpiry(expiry) {
  const [mm, yy] = (expiry || '').split('/');
  const month = Number(mm);
  const year = Number(`20${yy}`);
  if (!mm || !yy || month < 1 || month > 12) return 'Enter valid expiry (MM/YY).';
  const now = new Date();
  const exp = new Date(year, month - 1 + 1, 0); // last day of expiry month
  if (exp < now) return 'This card has expired.';
  return '';
}

function CardSection({ amount, processing, onPay }) {
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const cardInfo = detectCardType(cardNumber);
  const cvvLen = cardInfo?.cvvLen || 3;

  const validateField = (field, value) => {
    if (field === 'cardNumber') {
      const digits = (value || cardNumber).replace(/\s/g, '');
      if (!digits) return 'Card number is required.';
      if (digits.length < 13) return 'Enter a valid card number.';
      if (!luhn(digits)) return 'Invalid card number.';
      return '';
    }
    if (field === 'cardName') {
      const v = (value !== undefined ? value : cardName).trim();
      if (!v) return 'Name on card is required.';
      if (!/^[a-zA-Z ]+$/.test(v)) return 'Name should contain only letters.';
      return '';
    }
    if (field === 'expiry') {
      return validateExpiry(value !== undefined ? value : expiry);
    }
    if (field === 'cvv') {
      const v = value !== undefined ? value : cvv;
      if (!v || v.length < 3) return `CVV must be ${cvvLen} digits.`;
      return '';
    }
    return '';
  };

  const setTouching = (field) => {
    setTouched((p) => ({ ...p, [field]: true }));
    setErrors((p) => ({ ...p, [field]: validateField(field) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fields = ['cardNumber', 'cardName', 'expiry', 'cvv'];
    const errs = {};
    fields.forEach((f) => { errs[f] = validateField(f); });
    setErrors(errs);
    setTouched({ cardNumber: true, cardName: true, expiry: true, cvv: true });
    if (Object.values(errs).some(Boolean)) return;
    const [mm, yy] = expiry.split('/');
    onPay({
      method: 'card',
      cardDetails: {
        number: cardNumber.replace(/\s/g, ''),
        name: cardName.trim().toUpperCase(),
        expiryMonth: mm,
        expiryYear: `20${yy}`,
        cvv,
      },
    });
  };

  const handleCardNumberChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
    const spaced = raw.match(/.{1,4}/g)?.join(' ') || raw;
    setCardNumber(spaced);
    if (touched.cardNumber) setErrors((p) => ({ ...p, cardNumber: validateField('cardNumber', spaced) }));
  };

  return (
    <form onSubmit={handleSubmit} className={styles.collectForm} noValidate>
      <label className={styles.inputLabel} htmlFor="card-number">Card Number</label>
      <div className={styles.cardNumberWrapper}>
        <input
          id="card-number"
          type="text"
          inputMode="numeric"
          className={`${styles.textInput} ${errors.cardNumber ? styles.inputError : ''}`}
          placeholder="1234 5678 9012 3456"
          value={cardNumber}
          onChange={handleCardNumberChange}
          onBlur={() => setTouching('cardNumber')}
          disabled={processing}
          autoComplete="cc-number"
        />
        {cardInfo && <span className={styles.cardTypeIcon}>{cardInfo.icon}</span>}
      </div>
      {errors.cardNumber && <p className={styles.fieldError}>{errors.cardNumber}</p>}
      {!errors.cardNumber && cardInfo && (
        <p style={{ fontSize: '12px', color: '#4caf50', margin: '4px 0 0' }}>✓ {cardInfo.type}</p>
      )}

      <label className={styles.inputLabel} htmlFor="card-name">Name on Card</label>
      <input
        id="card-name"
        type="text"
        className={`${styles.textInput} ${errors.cardName ? styles.inputError : ''}`}
        placeholder="JOHN DOE"
        value={cardName}
        onChange={(e) => {
          setCardName(e.target.value);
          if (touched.cardName) setErrors((p) => ({ ...p, cardName: validateField('cardName', e.target.value) }));
        }}
        onBlur={() => setTouching('cardName')}
        disabled={processing}
        autoComplete="cc-name"
        style={{ textTransform: 'uppercase' }}
      />
      {errors.cardName && <p className={styles.fieldError}>{errors.cardName}</p>}

      <div className={styles.cardRow}>
        <div className={styles.cardRowField}>
          <label className={styles.inputLabel} htmlFor="card-expiry">Expiry (MM/YY)</label>
          <input
            id="card-expiry"
            type="text"
            inputMode="numeric"
            className={`${styles.textInput} ${errors.expiry ? styles.inputError : ''}`}
            placeholder="MM/YY"
            value={expiry}
            onChange={(e) => {
              const v = formatExpiry(e.target.value);
              setExpiry(v);
              if (touched.expiry) setErrors((p) => ({ ...p, expiry: validateField('expiry', v) }));
            }}
            onBlur={() => setTouching('expiry')}
            disabled={processing}
            autoComplete="cc-exp"
            maxLength={5}
          />
          {errors.expiry && <p className={styles.fieldError}>{errors.expiry}</p>}
        </div>
        <div className={styles.cardRowField}>
          <label className={styles.inputLabel} htmlFor="card-cvv">CVV</label>
          <input
            id="card-cvv"
            type="password"
            inputMode="numeric"
            className={`${styles.textInput} ${errors.cvv ? styles.inputError : ''}`}
            placeholder={cvvLen === 4 ? '••••' : '•••'}
            value={cvv}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, cvvLen);
              setCvv(v);
              if (touched.cvv) setErrors((p) => ({ ...p, cvv: validateField('cvv', v) }));
            }}
            onBlur={() => setTouching('cvv')}
            disabled={processing}
            autoComplete="cc-csc"
            maxLength={cvvLen}
          />
          {errors.cvv && <p className={styles.fieldError}>{errors.cvv}</p>}
        </div>
      </div>

      <p className={styles.collectHint}>
        🔒 Card details processed securely. 3D Secure may be required.
      </p>

      <button type="submit" className={styles.payBtn} disabled={processing}>
        {processing ? (
          <><span className={styles.spinnerSmall} /> Processing…</>
        ) : (
          `Pay ₹${Number(amount).toLocaleString('en-IN')}`
        )}
      </button>
    </form>
  );
}

export default CardSection;
